import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, Home, Users, Wrench, ShieldCheck, ExternalLink, Send, Settings } from 'lucide-react';
import { useAdminAuth } from '../../lib/adminAuth';
import { useAdminNotifications } from '../../lib/adminNotifications';
import { Button } from '../../components/ui/button';
import NotificationBell from '../../components/NotificationBell';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/properties', label: 'Properties', icon: Home },
  { to: '/admin/property-submissions', label: 'Property Submissions', icon: Send, badgeKey: 'property_submission' },
  { to: '/admin/interests', label: 'Property Interests', icon: Users, badgeKey: 'property_interest' },
  { to: '/admin/service-requests', label: 'Service Requests', icon: Wrench, badgeKey: 'service_request' },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminLayout = ({ children, title, actions }) => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { byType } = useAdminNotifications();

  const isActive = (item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
  const badgeFor = (item) => (item.badgeKey ? byType?.[item.badgeKey] || 0 : 0);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-gray-950 text-gray-300 flex-col">
        <Link to="/admin" className="flex items-center gap-2 h-16 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-[#9a1b2f] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-tight">SafeHouse</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-400">Admin Panel</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => {
            const badge = badgeFor(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item)
                    ? 'bg-[#9a1b2f] text-white'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-3">
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </span>
                {badge > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive(item) ? 'bg-white text-[#9a1b2f]' : 'bg-[#9a1b2f] text-white'}`}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-white">
            <ExternalLink className="w-3 h-3" /> View site
          </a>
          <div className="px-3 py-2 text-xs text-gray-400">Signed in as</div>
          <div className="px-3 pb-3 text-sm font-semibold text-white">{user?.username || 'admin'}</div>
          <Button size="sm" variant="outline" className="w-full bg-transparent border-white/10 text-gray-200 hover:bg-white/5 hover:text-white" onClick={() => { logout(); navigate('/admin/login'); }}>
            <LogOut className="w-3 h-3 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
          <div className="h-16 px-5 md:px-8 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="lg:hidden flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#9a1b2f] flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <span className="font-bold text-[#9a1b2f]">SafeHouse Admin</span>
              </div>
              <h1 className="hidden lg:block text-lg font-black text-gray-900 truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              {actions}
            </div>
          </div>
        </header>

        <div className="lg:hidden flex overflow-x-auto gap-1 px-4 py-2 bg-white border-b border-gray-100">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${isActive(item) ? 'bg-[#9a1b2f] text-white' : 'bg-gray-100 text-gray-700'}`}>
              <item.icon className="w-3 h-3" /> {item.label}
            </Link>
          ))}
        </div>

        <div className="p-5 md:p-8">
          <h1 className="lg:hidden text-lg font-black text-gray-900 mb-4">{title}</h1>
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
